/* ============================================================
 * N2T ship-week — fulfillment warehouse configuration
 *
 * Net to Table fulfills from Americold (National Stock Yards /
 * Fairmont City, IL — ZIP 62071) as of the 2026-08-17 cutover,
 * shipping FedEx Home Delivery. This file encodes that facility's
 * operational rules: the daily order cutoff hour, the warehouse
 * timezone, holiday closures, etc. Update the values here as
 * Americold policy changes.
 *
 * Prior origin was CC3PL (Wheeling, IL 60090) over UPS Ground.
 * Both the origin and the transit map were rebuilt for Americold +
 * FedEx — see nettotable/NetToTableShopify#199.
 *
 * NOTE: Premier Packing (Bellingham WA, 98225) is the seafood
 * processor — RFM-certified facility that receives, packs, and
 * freezes the catch. They do NOT do fulfillment. Don't confuse the
 * two. (98225 lingers as the `warehouse_zip` block setting on the
 * product templates; that attribute is informational only.)
 *
 * ──── How the math works (current rule, set by ops) ─────────
 *
 * Customer boxes always ARRIVE on the target arrival day
 * (currently Thursday). The math is arrival-first, not ship-first.
 * Algorithm:
 *
 *   effective_order_date =
 *     today                  if customer-local time < cutoff
 *     today + 1 day          otherwise
 *
 *   IF effective_order_date + transit_days ≤ this week's Thursday:
 *     → ship this week, arrival = this Thursday
 *   ELSE:
 *     → ship next week, arrival = next Thursday
 *
 * Ship date is computed as (arrival_date - transit_days). Because
 * transit varies by zone, the ship day VARIES but arrival is always
 * Thursday — the customer always sees a Thursday arrival week in
 * the pill.
 *
 * Americold's "no Friday ship / Thursday is the last ship day"
 * constraint is structurally satisfied by this arrival-anchored
 * math: with effective transit of 1/2/3 days the only reachable
 * ship weekdays are Wed/Tue/Mon respectively. No Friday ship is
 * reachable, so no code change is needed for that rule.
 *
 * Loaded BEFORE ship-week.js (see ship-week-display.liquid).
 * Consumed via window.N2T_SHIP_WAREHOUSE.
 *
 * TODO (ops): confirm the following with the Americold contact:
 *   - Daily cutoff hour (carried over from CC3PL — 10am Central)
 *   - 2026 closure / holiday calendar (blackout_dates)
 *   - Whether the >3-ground-day → 2nd Day Air upgrade still holds
 *     under FedEx (the tier shrank from 12.5% to 7.5% of ZIPs)
 * ============================================================ */
(function () {
  'use strict';

  window.N2T_SHIP_WAREHOUSE = {
    /* ---------- Origin (Americold warehouse) ----------
     * Documentation only — no code reads these two fields. The
     * authoritative origin is baked into the transit map itself
     * (assets/ship-week-zones-data.json), which was pulled from
     * the FedEx Rates & Transit Times API for origin 62071.
     * FedEx echoed the origin back as
     * {stateOrProvinceCode: IL, postalCode: 62071, locationNumber: 632}.
     */
    origin_zip: '62071',                  // Americold — National Stock Yards / Fairmont City, IL
    origin_label: 'Fairmont City, IL',
    origin_timezone: 'America/Chicago',   // Central Time (unchanged from Wheeling)

    /* ---------- Daily cutoff hour ----------
     * Hour (24h, in warehouse timezone) before which orders are
     * processed for SAME-day shipping. After this hour, orders
     * roll over to the next-day batch.
     *
     * 10am Central — carried over from CC3PL, NOT yet re-confirmed
     * with Americold. See the TODO above.
     */
    daily_cutoff_hour: 10,

    /* ---------- Air-upgrade policy ----------
     * Wild seafood is perishable — ground shipments longer than
     * `air_upgrade_threshold_days` are upgraded to 2nd Day Air to
     * keep cold-chain integrity.
     *
     * Current policy: anything > 3 ground days ships 2nd Day Air.
     * Under FedEx from 62071 this tier is 3,077 ZIPs (7.5%), down
     * from 5,120 (12.5%) under UPS from 60090 — a real freight-cost
     * and cold-chain change that ops should re-confirm.
     *
     * Applied at zone lookup time — `window.N2T_SHIP_ZONES.lookup(zip)`
     * returns `transit_days` already adjusted by this policy.
     * `ground_transit_days` is preserved for reference.
     */
    air_upgrade_threshold_days: 3,
    air_transit_days: 2,

    /* ---------- Destinations we do NOT ship to ----------
     * Mirrors the store's Shopify Domestic delivery zone, which covers
     * the 48 contiguous states + DC — NOT Alaska, NOT Hawaii, and no US
     * territories. An order to any of these gets no shipping rate at
     * checkout, so the pill must never quote an arrival week for one.
     *
     * This is business policy, deliberately kept OUT of the transit map:
     * `ship-week-zones-data.json` stays a faithful carrier artifact (it
     * does contain AK and HI transit times), and the store's shipping
     * footprint is expressed here, where it can change without a re-pull.
     *
     * Without this, all 409 AK/HI ZIPs in the map exceed the 3-day ground
     * threshold, get auto-upgraded to "2nd Day Air", and the pill promises
     * an Alaskan shopper the same Thursday arrival as a Chicago one.
     *
     * Format: [zip_low, zip_high, label] — inclusive, compared numerically.
     * Verified non-overlapping with every served-state ZIP in the map.
     * (00501, Holtsville NY, sits below the 00600 floor — do not lower it.)
     *
     * If the delivery profile ever gains AK/HI, delete the row: the
     * transit data is already there and the pill starts working for them.
     */
    unserved_zip_ranges: [
      [600, 999, 'Puerto Rico and the U.S. Virgin Islands'],
      [96701, 96798, 'Hawaii'],
      [96799, 96799, 'American Samoa'],   // sits INSIDE the HI block — split around it
      [96800, 96898, 'Hawaii'],
      [96900, 96999, 'Guam and the Northern Mariana Islands'],
      [99501, 99999, 'Alaska'],
    ],

    /* ---------- Blackout dates ----------
     * Days the warehouse does NOT ship (holidays, planned closures,
     * weather events). Format: ISO date strings "YYYY-MM-DD" in the
     * warehouse timezone.
     *
     * If the target arrival Thursday is in this list, the math pushes
     * arrival forward by one week.
     *
     * Update with Americold's confirmed closure calendar.
     */
    blackout_dates: [
      // '2026-11-27',  // Thanksgiving Day
      // '2026-12-25',  // Christmas Day
      // '2027-01-01',  // New Year's Day
      // Add Americold-confirmed closures here.
    ],

    /* ---------- Pack lead time (reserved for future) ----------
     * Hours required between order placement and ship-out.
     * Currently NOT used — placeholder for v3 logic if Americold
     * adds a queue / batching delay before any package ships.
     */
    pack_lead_hours: 0,
  };
})();
